<template>
  <section class="media-module-component module-container">
    <div class="media-inner">
      <header
        :id="selectedCategory === 'General Releases' ? 'media-filters' : null"
        ref="moduleTop"
        class="media-header">
        <div
          v-if="props.data?.content"
          class="media-intro"
          v-html="props.data.content"></div>

        <MediaResourcesFilter
          v-if="yearOptions.length > 1"
          v-model="selectedYear"
          label="Year"
          :options="yearOptions" />
      </header>

      <div
        v-if="isLoading"
        class="news-list"
        aria-label="Loading news"
        aria-busy="true">
        <article
          v-for="item in itemsPerPage"
          :key="`news-skeleton-${item}`"
          class="news-card news-card-skeleton">
          <span class="skeleton-date"></span>
          <span class="skeleton-accent"></span>
          <span class="skeleton-title"></span>
          <span class="skeleton-title short"></span>
        </article>
      </div>

      <p
        v-else-if="errorMessage"
        class="news-message"
        role="alert">
        {{ errorMessage }}
      </p>

      <p
        v-else-if="!filteredNews.length"
        class="news-message">
        No {{ selectedCategory.toLowerCase() }} were found for this year.
      </p>

      <div
        v-else
        class="news-list">
        <router-link
          v-for="news in paginatedNews"
          :key="news.id"
          class="news-card"
          :to="newsRoute(news)">
          <div class="news-card-copy">
            <time :datetime="news.dateUTC">
              {{ formatDate(news.dateUTC) }}
            </time>
            <span
              class="news-accent"
              aria-hidden="true"></span>
            <h3>{{ news.title }}</h3>
          </div>

          <span
            class="news-link-icon"
            aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M7 17 17 7M9 7h8v8" />
            </svg>
          </span>
        </router-link>
      </div>

      <nav
        v-if="totalPages > 1"
        class="news-pagination"
        aria-label="News pagination">
        <button
          type="button"
          aria-label="Previous page"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)">
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20">
            <path d="m12.5 5-5 5 5 5" />
          </svg>
        </button>

        <button
          v-for="page in paginationPages"
          :key="`news-page-${page}`"
          type="button"
          :class="{ active: currentPage === page }"
          :aria-label="`Page ${page}`"
          :aria-current="currentPage === page ? 'page' : null"
          @click="goToPage(page)">
          {{ page }}
        </button>

        <button
          type="button"
          aria-label="Next page"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)">
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20">
            <path d="m7.5 5 5 5-5 5" />
          </svg>
        </button>
      </nav>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "MediaComponent",
  "CLASSNAME": "media-module-component",
  "URL": "http://vuevitewp.local/test"
 }
#ENDMETA */

import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { useStore } from '@/stores/main'
import MediaResourcesFilter from '@/components/modules/MediaResourcesFilter.vue'

const props = defineProps(['data'])
const store = useStore()

const newsItems = ref([])
const selectedYear = ref('all')
const currentPage = ref(1)
const isLoading = ref(true)
const errorMessage = ref('')
const moduleTop = ref(null)
const itemsPerPage = 6

const selectedCategory = computed(
  () => props.data?.category || 'General Releases'
)

const availableYears = computed(() => {
  const years = newsItems.value
    .map((news) => dateYear(news.dateUTC))
    .filter(Boolean)

  return [...new Set(years)].sort((a, b) => Number(b) - Number(a))
})

const yearOptions = computed(() => [
  { value: 'all', label: 'All Years' },
  ...availableYears.value.map((year) => ({ value: year, label: year })),
])

const filteredNews = computed(() =>
  selectedYear.value === 'all'
    ? newsItems.value
    : newsItems.value.filter(
        (news) => dateYear(news.dateUTC) === selectedYear.value
      )
)

const totalPages = computed(() =>
  Math.ceil(filteredNews.value.length / itemsPerPage)
)

const paginatedNews = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return filteredNews.value.slice(start, start + itemsPerPage)
})

const paginationPages = computed(() => {
  const visibleCount = Math.min(5, totalPages.value)
  const maxStart = Math.max(1, totalPages.value - visibleCount + 1)
  const start = Math.min(Math.max(1, currentPage.value - 2), maxStart)

  return Array.from({ length: visibleCount }, (_, index) => start + index)
})

const dateValue = (value) => {
  if (!value) return null
  const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? value
    : `${value}Z`
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

const dateYear = (value) => {
  const date = dateValue(value)
  return date ? String(date.getUTCFullYear()) : ''
}

const formatDate = (value) => {
  const date = dateValue(value)
  if (!date) return ''

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

const slugify = (value) =>
  String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const newsRoute = (news) => ({
  name: 'SingleNews',
  params: {
    id: String(news.id),
    slug: slugify(news.title),
  },
})

const NEWS_REFRESH_INTERVAL_MS = 90 * 1000
let newsRefreshTimer = null

const loadNews = async ({ silent = false } = {}) => {
  if (!silent) {
    isLoading.value = true
    errorMessage.value = ''
  }

  try {
    const siteUrl = String(store.site.url || '').replace(/\/$/, '')
    const endpoint = `${siteUrl}/wp-json/revmed/v1/news?category=${encodeURIComponent(
      selectedCategory.value
    )}`
    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })

    if (!response.ok) throw new Error(`News request failed: ${response.status}`)

    const payload = await response.json()
    newsItems.value = Array.isArray(payload.data) ? payload.data : []

    if (!silent) {
      selectedYear.value = availableYears.value[0] || 'all'
      currentPage.value = 1
    }
  } catch (error) {
    console.error('Unable to load GCS news.', error)
    if (!silent) {
      errorMessage.value =
        'News is temporarily unavailable. Please try again later.'
    }
  } finally {
    if (!silent) {
      isLoading.value = false
    }
  }
}

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page

  nextTick(() => {
    moduleTop.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

watch(selectedYear, () => {
  currentPage.value = 1
})

onBeforeMount(loadNews)

onMounted(() => {
  newsRefreshTimer = setInterval(() => {
    loadNews({ silent: true })
  }, NEWS_REFRESH_INTERVAL_MS)
})

onUnmounted(() => {
  if (newsRefreshTimer) {
    clearInterval(newsRefreshTimer)
    newsRefreshTimer = null
  }
})
</script>

<style lang="scss" scoped>
.media-module-component {
  @apply w-full bg-functional px-5 py-12 md:px-10 lg:px-14 lg:py-[75px];

  + .module-container {
    @apply mt-12 md:pt-[0px];
  }

  .media-inner {
    @apply mx-auto w-full max-w-[1200px] rounded-[20px] bg-white/70 px-5 py-10 md:px-10 lg:px-[75px] lg:py-[70px];

    .media-header {
      @apply mb-10 grid scroll-mt-32 grid-cols-1 gap-8 lg:mb-[50px] lg:grid-cols-[minmax(0,1fr)_230px] lg:items-end lg:gap-[90px];

      :deep(.filter-field) {
        background: rgba(30, 107, 92, 0.06);
      }

      .media-intro:deep() {
        h1,
        h2,
        h3 {
          @apply mb-7 font-FKDisplay text-[30px] font-normal leading-tight text-balanced lg:text-[40px];
        }

        p {
          @apply mb-0 max-w-[650px] font-FKGrotesk text-[16px] leading-[1.2] text-humble;
        }

        a {
          @apply underline underline-offset-2;
        }
      }
    }

    .news-list {
      @apply flex flex-col gap-5;

      .news-card {
        @apply relative flex min-h-[190px] items-start justify-between gap-6 rounded-[15px] bg-white px-6 py-7 no-underline shadow-[0_5px_12px_rgba(14,67,67,0.12)] transition-transform duration-200 hover:-translate-y-0.5 md:px-8;

        .news-card-copy {
          @apply min-w-0;

          time {
            @apply block font-FKGrotesk text-[13px] leading-tight text-gray_green;
          }

          .news-accent {
            @apply my-7 block h-[3px] w-[200px] max-w-full;
            background: linear-gradient(
              90deg,
              #70ffe5 0%,
              #70ffe5 50%,
              #13d377 50%,
              #13d377 100%
            );
          }

          h3 {
            @apply max-w-[900px] font-FKGrotesk text-[20px] font-normal leading-[1.15] tracking-[-0.02em] text-balanced md:text-[22px];
          }
        }

        .news-link-icon {
          @apply flex h-[59px] w-[53px] shrink-0 items-center justify-center bg-progressive;
          clip-path: polygon(50% 0, 100% 20%, 100% 80%, 50% 100%, 0 80%, 0 20%);

          svg {
            @apply h-6 w-6;

            path {
              fill: none;
              stroke: currentColor;
              stroke-linecap: round;
              stroke-linejoin: round;
              stroke-width: 1.5;
            }
          }
        }

        &.news-card-skeleton {
          @apply flex-col items-start justify-center;

          span {
            @apply block animate-pulse rounded bg-[#d7dcdb];
          }

          .skeleton-date {
            @apply h-3 w-24;
          }

          .skeleton-accent {
            @apply h-[3px] w-[200px];
          }

          .skeleton-title {
            @apply h-5 w-full;

            &.short {
              @apply w-2/3;
            }
          }
        }
      }
    }

    .news-message {
      @apply rounded-[15px] bg-white px-6 py-12 text-center font-FKGrotesk text-[18px] text-balanced;
    }

    .news-pagination {
      @apply mx-auto mt-[50px] flex w-fit items-center gap-1.5 rounded-[14px] border-[5px] border-gray_green_trans bg-functional px-3 py-2;

      button {
        @apply flex h-8 min-w-[32px] items-center justify-center rounded-full border-0 bg-transparent px-2 font-FKGrotesk text-[13px] text-humble transition-colors hover:bg-white disabled:pointer-events-none disabled:opacity-30;

        &.active {
          @apply border border-balanced bg-white;
        }

        svg {
          @apply h-5 w-5 text-progressive;

          path {
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.5;
          }
        }
      }
    }
  }
}

@media (max-width: 639px) {
  .media-module-component {
    .media-inner {
      .news-list {
        .news-card {
          @apply min-h-[220px] items-start;

          .news-link-icon {
            @apply h-[47px] w-[43px];
          }
        }
      }
    }
  }
}
</style>
